import get from 'lodash/get';

import {
  getActiveDesign,
  getActiveSpace,
  getPackagesData,
  getSelectedHotspotProduct
} from 'redux/Studio';

import { getSearchKey } from 'redux/StudioProductBrowser';

import {
  getUserId,
  userEmail as getUserEmail,
  userIsLoggedIn as getUserIsLoggedIn,
  userStyleGenome as getUserStyleGenome
} from 'redux/User';

import { memoize } from 'utils/utils';

import warehouse from 'services/warehouse';
import ProductSearch from 'services/ProductSearch';
import { CB2_URL_QUERY_CODE } from 'utils/constants';

import * as modsyFacebook from './facebook';
import * as modsyTikTok from './tiktok';
import * as modsyGoogle from './google';
import * as modsyPostie from './postie';
import * as modsySegment from './segment';
import * as modsyTatari from './TatariEvents';
import modsyMixpanel from './modsy_mixpanel';
import { fetchProductBrowserSearchIndex, getAlternativeID } from './utils';

/**
 * General Analytics comments.
 *
 * We are taking advantage of redux here to help with analytics verification.
 * We dispatch tracking events which allows us to then check dev tools to see
 * whether or not our events our firing as expected.  However, there is no
 * corresponding reducer or state changes at this time.
 */

/**
  * Tracking for page views.
  */
export const trackPageView = ({ location, isInitialPageLoad }) => dispatch => {
  // Mixpanel
  switch (location.pathname) {
    case '/':
      modsyMixpanel.trackPageView();
      break;

    case '/project/room':
    case '/project/goal':
    case '/project/furniture':
    case '/project/budget':
    case '/project/style':
    case '/project/account':
    case '/project/your-recommendation':
      modsyMixpanel.__mixpanelTrackEvent('Project Questions Page View');
      break;

    default:
      break;
  }

  // Google, Segment, and FB pixels/tags fire their own pageviews on inital load.
  if (!isInitialPageLoad) {
    modsyGoogle.trackPageView(location);
    modsySegment.trackPageView();
    modsyTikTok.trackPageView();
    // facebook pixel automaically tracks all page views except on init
    // we can disable this, but may lose some features
    // "You should leave the listener on and let it track PageViews when you SPA's URL changes.
    // This Facebook Pixel feature also manages the referrers and URLs so you can also use and
    // track Custom URL conversions."
    // source: https://developers.facebook.com/ads/blog/post/2017/05/29/tagging-a-single-page-application-facebook-pixel
    // modsyFacebook.trackPageView(location);
  }
};

/**
 * Tracking for user login events.
 */
export const userLoggedIn = ({
  userId,
  email,
  firstName,
  lastName,
  landing,
  created,
  isAdmin,
  dateJoined
}) => dispatch => {
  modsyMixpanel.trackLogin(
    userId,
    email,
    firstName,
    lastName,
    landing,
    created,
    isAdmin || 0
  );
  modsySegment.trackAuth({
    userId,
    traits: {
      createdAt: dateJoined,
      email,
      firstName,
      isAdmin: isAdmin || false,
      landing,
      lastName
    }
  });
  dispatch && dispatch({
    type: 'analytics/TRACK_LOGIN',
    payload: { email, userId }
  });
};

/**
 * Tracking for sign up events.
 */
export const userSignedUp = ({
  userId,
  email,
  firstName,
  lastName,
  created,
  dateJoined,
  from,
  packageName = ''
}, retailPartner) => dispatch => {
  modsyMixpanel.trackSignup(
    userId,
    email,
    firstName,
    lastName,
    null,
    created
  );
  modsySegment.trackAuth({
    userId,
    traits: {
      createdAt: created,
      email,
      firstName,
      isAdmin: false,
      landing: null,
      lastName
    }
  });

  userRegistered({ userId, email, from });

  modsyFacebook.trackRegistration(userId);
  modsyTikTok.trackRegistration();
  modsyGoogle.trackSignup({ email, from, packageName });
  modsyPostie.trackEmailRegister(email);
  const isCB2User = retailPartner && retailPartner.slug === 'cb2';
  if (isCB2User) {
    modsyFacebook.cb2TrackSignUp();
  }

  dispatch && dispatch({
    type: 'analytics/TRACK_SIGNUP',
    payload: { email, userId, isCB2User }
  });
};

export const userRegistered = ({ userId, email, from }) => {
  modsySegment.trackEvent({
    event: 'user_registered',
    messageId: 'registration.' + userId,
    properties: {
      id: userId,
      email: email,
      source: from,
      event_id: 'registration.' + userId,
      eventID: 'registration.' + userId,
      external_id: userId
    }
  });
};

/**
 * Tracking adding product to cart from PDP
 */
export const userAddedProductFromPDP = (email, product) => dispatch => {
  modsyGoogle.trackEvent('PDP', 'add product to cart', email);

  dispatch && dispatch({
    type: 'analytics/TRACK_PDP_ADD_TO_CART',
    payload: { email, product }
  });
};

/**
 * Tracking for package purchase customer conversion events.
 */
export const packagePurchased = ({
  itemName,
  orderTotal,
  retailPartner,
  orderInfo,
  orderNumber,
  email,
  spaceId,
  direct,
  userId,
  designfeedParams,
  isGiftCard
}) => dispatch => {
  modsyGoogle.trackPackagePurchase({
    itemName,
    orderTotal,
    orderNumber
  });

  modsyGoogle.trackDirectPackagePurchase({
    itemName,
    orderTotal,
    orderNumber,
    isGiftCard
  });

  modsyFacebook.trackPackagePurchase({
    orderTotal,
    orderNumber,
    itemName,
    userId
  });

  modsyTikTok.trackPackagePurchase({
    orderTotal
  });

  modsyPostie.trackPackagePurchase({
    email,
    orderInfo,
    orderNumber,
    orderTotal
  });

  const payload = {
    directToCheckout: direct,
    packageType: itemName,
    paymentTotal: orderTotal,
    orderNumber,
    spaceId,
    userId
  };

  modsySegment.trackPackageOrderComplete({ ...payload, designfeedParams });
  modsyTatari.trackPackageOrderComplete(payload);

  if (retailPartner === CB2_URL_QUERY_CODE) {
    modsyFacebook.cb2TrackPackagePurchase();
  }

  dispatch && dispatch({
    type: 'analytics/PACKAGE_PURCHASED',
    payload: { itemName, orderTotal, retailPartner }
  });
};

export const packageCouponApplied = (payload) => dispatch => {
  modsySegment.trackPackageCouponApplied(payload);
  modsyTatari.trackPackageCouponApplied(payload);
  dispatch && dispatch({
    type: 'analytics/PACKAGE_COUPON_APPLIED',
    payload
  });
};

export const packageProjectStarted = (payload) => dispatch => {
  modsySegment.trackPackageProjectStarted(payload);
  modsyTatari.trackPackageProjectStarted(payload);
  dispatch && dispatch({
    type: 'analytics/PACKAGE_PROJECT_STARTED',
    payload
  });
};

/**
* Tracking for PPO onboarding
*/
export const ppoDashboardViewed = ({
  packageType = '',
  stepsCompleted = [],
  studioId = null
}) => dispatch => {
  modsySegment.trackPPODashboardViewed({ packageType, stepsCompleted, studioId });
  dispatch(trackEvent({
    event: 'ppo_dashboard_view',
    data: {
      studio_id: studioId,
      package_type: packageType,
      steps_completed: stepsCompleted
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_DASHBOARD_VIEWED',
    payload: {
      packageType,
      stepsCompleted,
      studioId
    }
  });
};

export const ppoPhotoAndMeasurementStarted = ({
  studioId = null
}) => dispatch => {
  modsySegment.trackPPOPhotoAndMeasurementStarted({ studioId });
  dispatch(trackEvent({
    event: 'ppo_p&m_started',
    data: {
      studio_id: studioId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_PM_STARTED',
    payload: {
      studioId
    }
  });
};

export const ppoExperimentalActivated = ({
  userId = null,
  studioId = null,
  experimentalFeatureFlag = null
}) => dispatch => {
  modsySegment.trackPPOExperimentalActivated({ userId, studioId, experimentalFeatureFlag });
  dispatch(trackEvent({
    event: 'ppo_p&m_experimental_activated',
    data: {
      user_id: userId,
      studio_id: studioId,
      experimental_featureFlag: experimentalFeatureFlag
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_EXPERIMENTAL_ACTIVATED',
    payload: {
      userId,
      studioId,
      experimentalFeatureFlag
    }
  });
};

export const ppoMeasurementsUpdated = ({
  studioId = null,
  measurementEntered = ''
}) => dispatch => {
  modsySegment.trackPPOMeasurementsUpdated({ studioId, measurementEntered });
  dispatch(trackEvent({
    event: 'ppo_p&m_measurements',
    data: {
      studio_id: studioId,
      measurements_entered: measurementEntered
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_MEASUREMENTS_UPDATED',
    payload: {
      studioId,
      measurementEntered
    }
  });
};

export const ppoMeasurementsWarnings = ({
  studioId = null,
  measurementEntered = '',
  warningMessageShown = ''
}) => dispatch => {
  modsySegment.trackPPOMeasurementsWarnings({ studioId, measurementEntered, warningMessageShown });
  dispatch(trackEvent({
    event: 'ppo_p&m_measurements_warnings',
    data: {
      studio_id: studioId,
      measurements_entered: measurementEntered,
      warning_message_shown: warningMessageShown
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_MEASUREMENTS_WARNINGS',
    payload: {
      studioId,
      measurementEntered,
      warningMessageShown
    }
  });
};

export const ppoPhotosInstructionsSelected = ({
  spaceId,
  userId
}) => dispatch => {
  modsySegment.trackPPOPhotosInstructionsSelected({
    spaceId,
    userId
  });
  dispatch(trackEvent({
    event: 'how_to_take_photos_video_click',
    data: {
      studio_id: spaceId,
      user_id: userId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_HOW_TO_TAKE_PHOTOS_VIDEO_CLICK',
    payload: {
      studio_id: spaceId,
      user_id: userId
    }
  });
};

export const ppoMeasurementsSelected = ({
  spaceId,
  userId
}) => dispatch => {
  modsySegment.trackPPOMeasurementsSelected({
    spaceId,
    userId
  });
  dispatch(trackEvent({
    event: 'how_to_measure_room_video_click',
    data: {
      space_id: spaceId,
      user_id: userId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_HOW_TO_MEASURE_ROOM_VIDEO_CLICK',
    payload: {
      space_id: spaceId,
      user_id: userId
    }
  });
};

export const ppoTrickySpacesSelected = ({
  spaceId,
  userId
}) => dispatch => {
  modsySegment.trackPPOTrickySpacesSelected({
    spaceId,
    userId
  });
  dispatch(trackEvent({
    event: 'how_to_take_photos_tricky_space_video_click',
    data: {
      space_id: spaceId,
      user_id: userId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_HOW_TO_TAKE_PHOTOS_TRICKY_SPACE_VIDEO_CLICK',
    payload: {
      space_id: spaceId,
      user_id: userId
    }
  });
};

export const ppoFloorPlanUploaded = ({
  studioId = null
}) => dispatch => {
  modsySegment.trackPPOFloorPlanUploaded({ studioId });
  dispatch(trackEvent({
    event: 'ppo_p&m_upload_floorplan',
    data: {
      studio_id: studioId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_FLOOR_PLAN_UPLOADED',
    payload: {
      studioId
    }
  });
};

export const ppoPhotosUploaded = ({
  studioId = null,
  numberOfPhotos = 0 // Total Number of uploaded photos after this addition/removal
}) => dispatch => {
  modsySegment.trackPPOPhotosUploaded({ studioId, numberOfPhotos });
  dispatch(trackEvent({
    event: 'ppo_p&m_upload_photos',
    data: {
      studio_id: studioId,
      number_of_photos: numberOfPhotos
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_PHOTOS_UPLOADED',
    payload: {
      studioId,
      numberOfPhotos
    }
  });
};

export const ppoPhotosWarnings = ({
  studioId = null,
  warningMessageShown = ''
}) => dispatch => {
  modsySegment.trackPPOPhotosWarnings({ studioId, warningMessageShown });
  dispatch(trackEvent({
    event: 'ppo_p&m_photos_warnings',
    data: {
      studio_id: studioId,
      warning_message_shown: warningMessageShown
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_PHOTOS_WARNINGS',
    payload: {
      studioId,
      warningMessageShown
    }
  });
};

export const ppoPhotosSubmitted = ({
  studioId = null,
  numberOfPhotos = 0
}) => dispatch => {
  modsySegment.trackPPOPhotosSubmitted({ studioId, numberOfPhotos });
  dispatch(trackEvent({
    event: 'ppo_p&m_photos_submitted',
    data: {
      studio_id: studioId,
      number_of_photos: numberOfPhotos
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_PHOTOS_SUBMITTED',
    payload: {
      studioId,
      numberOfPhotos
    }
  });
};

export const ppoWhoLivesHereSubmitted = ({
  studioId = null,
  aboutYou = '',
  whoLivesHere = {},
  additionalInfo = ''
}) => dispatch => {
  modsySegment.trackPPOWhoLivesHereSubmitted({
    studioId,
    aboutYou,
    whoLivesHere,
    additionalInfo
  });
  dispatch(trackEvent({
    event: 'ppo_who_lives_here_started',
    data: {
      studio_id: studioId,
      about_you: aboutYou,
      who_lives_here: whoLivesHere,
      additional_info: additionalInfo
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_WHO_LIVES_HERE_STARTED',
    payload: {
      studioId,
      aboutYou,
      whoLivesHere,
      additionalInfo
    }
  });
};

export const ppoVisionStarted = () => (dispatch, getState) => {
  const state = getState();
  const formValues = get(state.form, 'yourVisionForm.values');
  const productCategories = get(state.productCategories, 'data.results');
  const FLEXIBILITY_STRINGS = {
    '1': 'do_not_exceed',
    '2': 'use_as_guideline',
    '3': 'prioritize_quality'
  };
  const TV_STRINGS = {
    '0': 'Small',
    '1': 'Medium',
    '2': 'Large'
  };

  let keyPieces = formValues.intendedPieces;
  if (keyPieces.length) {
    keyPieces = keyPieces.filter(pc => pc).map(pc => {
      if (productCategories && productCategories.length) {
        const foundCat = productCategories.find(cat => cat.id === pc.categoriesByRoomType);
        if (foundCat) {
          pc.name = foundCat.productType;
          pc.popularIn = foundCat.popularIn.includes(foundCat.currentRoomType);
        }
      }
      delete pc.maxBudget;
      delete pc.minBudget;
      return pc;
    });
  }

  // track Segment event
  const payload = {
    brands: formValues.furnitureBrands,
    colorIntensity: formValues.colorIntensity,
    colorPreference: formValues.colorFamily,
    consultation: formValues.consultation,
    furnitureBudget: formValues.intendedPieces,
    inspiration: formValues.inspirationLinks,
    keyPieces,
    keyPiecesAdditionalInfo: !!formValues.intendedPiecesOther.length,
    notes: formValues.styleNotes,
    studioId: formValues.studio,
    spaceUsage: formValues.includeInLayout,
    specialConsiderations: formValues.layoutConsiderations,
    totalBudget: formValues.budgetMax,
    flexibility: FLEXIBILITY_STRINGS[formValues.stickToBudget],
    doNotExceedAmount: ('$' + formValues.doNotExceed),
    exceedsAggregate: !!formValues.aggregate && formValues.budgetMax > formValues.aggregate,
    doYouWantTv: formValues.doYouWantTv,
    whatSizeTv: TV_STRINGS[formValues.whatSizeTv],
    whereToPlaceTv: formValues.whereToPlaceTv,
    additionalMedia: formValues.additionalMedia
  };
  modsySegment.trackPPOVisionStarted(payload);
  dispatch && dispatch({
    type: 'analytics/PPO_VISION_STARTED',
    payload
  });
};

export const ppoInspirationLinkErrors = (errors = [], links = []) => (dispatch, getState) => {
  const errorLinks = errors.reduce((acc, error, index) => {
    if (error) { acc.push(links[index]); }
    return acc;
  }, []);

  // track Segment event
  const payload = {
    links: errorLinks
  };
  modsySegment.trackPPOInspirationLinkErrors(payload);
  dispatch && dispatch({
    type: 'analytics/PPO_INSPIRATION_LINKERRORS',
    payload
  });
};

export const ppoExistingFurnitureStarted = (payload) => dispatch => {
  modsySegment.trackPPOExistingFurnitureStarted(payload);
  dispatch && dispatch({
    type: 'analytics/PPO_EXISTING_FURNITURE_STARTED',
    payload
  });
};

export const ppoProjectSubmitted = ({
  studioId,
  userEmail
}) => dispatch => {
  modsySegment.trackPPOProjectSubmitted({ studioId, userEmail });
  dispatch(trackEvent({
    event: 'PPO: Project Submitted',
    data: {
      user_email: userEmail,
      styleboard_id: studioId
    }
  }));
  dispatch && dispatch({
    type: 'analytics/PPO_PROJECT_SUBMITTED',
    payload: {
      studioId
    }
  });
};

export const trackCustomPropPaymentComplete = (payload) => dispatch => {
  modsySegment.trackCustomPropPaymentComplete(payload);
  dispatch && dispatch({
    type: 'analytics/CUSTOM_PROP_PAYMENT_COMPLETE',
    payload
  });
};

/**
* Tracking for StyleQuiz results
*/
export const styleQuizSurveyResults = payload => {
  modsySegment.trackSurveyResult(payload);
};

export const styleQuizProductRate = (payload) => dispatch => {
  modsySegment.trackStyleQuizProductRate(payload);
  dispatch && dispatch({
    type: 'analytics/SQ_PRODUCT_RATE',
    payload
  });
};

export const styleQuizComplete = (payload) => dispatch => {
  modsySegment.trackStyleQuizComplete(payload);
  dispatch && dispatch({
    type: 'analytics/SQ_COMPLETE',
    payload
  });
};

export const trackShowroomStyleQuizStarted = (payload) => dispatch => {
  modsySegment.trackShowroomStyleQuizStarted(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_SQ_STARTED',
    payload
  });
};

export const trackShowroomStyleQuizProductRate = (payload) => dispatch => {
  modsySegment.trackShowroomStyleQuizProductRate(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_SQ_PRODUCT_RATE',
    payload
  });
};

export const trackShowroomStyleQuizComplete = (payload) => dispatch => {
  modsySegment.trackShowroomStyleQuizComplete(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_SQ_COMPLETE',
    payload
  });
};

/**
* Tracking for Showroom Onboarding events
*/

export const trackShowroomFloorTypeSelected = (payload) => dispatch => {
  modsySegment.trackShowroomFloorTypeSelected(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_FLOOR_TYPE_SELECTED',
    payload
  });
};

export const trackShowroomWallColorSelected = (payload) => dispatch => {
  modsySegment.trackShowroomWallColorSelected(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_WALL_COLOR_SELECTED',
    payload
  });
};

export const trackShowroomUpgradeSelected = (payload) => dispatch => {
  modsySegment.trackShowroomUpgradeSelected(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_UPGRADE_SELECTED',
    payload
  });
};

export const trackShowroomUpgradeBannerExpanded = (payload) => dispatch => {
  modsySegment.trackShowroomUpgradeBannerExpanded(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_UPGRADE_BANNER_EXPANDED',
    payload
  });
};

export const trackShowroomProjectDetailsCompleted = (payload) => dispatch => {
  modsySegment.trackShowroomProjectDetailsCompleted(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_PROJECT_DETAILS_COMPLETED',
    payload
  });
};

export const trackShowroomPendingDesignsVideoWatched = (payload) => dispatch => {
  modsySegment.trackShowroomPendingDesignsVideoWatched(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_PENDING_DESIGNS_VIDEO_WATCHED',
    payload
  });
};

export const trackShowroomViewDesigns = (payload) => dispatch => {
  modsySegment.trackShowroomViewDesigns(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_VIEW_DESIGNS',
    payload
  });
};

export const trackShowroomRestyle = (payload) => dispatch => {
  modsySegment.trackShowroomRestyle(payload);
  dispatch && dispatch({
    type: 'analytics/SHOWROOM_RESTYLE',
    payload
  });
};

/**
 * Tracking for Merchandise orders
 */
export const trackStudioCartCouponApplied = (payload) => dispatch => {
  modsySegment.trackStudioCartCouponApplied(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_CART_COUPON_APPLIED',
    payload
  });
};

export const merchandisePurchased = ({
  shippingInfo,
  designId,
  displayedInfo,
  items,
  orderNumber,
  paymentMethod,
  products,
  shippingCost,
  spaceId,
  total
}) => dispatch => {
  let data = { orderNum: orderNumber, total };
  if (paymentMethod) { data.paymentMethod = paymentMethod; }

  modsyMixpanel.__mixpanelTrackEvent('Order Success', data);
  modsyGoogle.trackMerchPurchase({ orderNumber, displayedInfo });
  modsyFacebook.trackMerchPurchase({ displayedInfo });
  modsyTikTok.trackMerchPurchase({ displayedInfo });
  modsyPostie.trackMerchPurchase({ displayedInfo, shippingInfo, orderNumber });

  const payload = {
    cartTotal: total,
    items,
    designId,
    paymentOption: paymentMethod,
    products,
    spaceId,
    shipAddress: shippingInfo.streetAddress,
    shipCost: shippingCost,
    shipState: shippingInfo.state,
    shipZip: shippingInfo.zip
  };

  modsySegment.trackStudioCartOrderCompleted(payload);

  dispatch && dispatch({
    type: 'analytics/MERCHANDISE_PURCHASED',
    payload: data
  });
};

export const merchandisePurchaseFailed = ({ items, message, paymentMethod }) => dispatch => {
  let data = { items, reason: message };
  if (paymentMethod) { data.paymentMethod = paymentMethod; }

  modsyMixpanel.__mixpanelTrackEvent('Order Fail', data);

  modsySegment.trackStudioCartOrderFailed(data);

  dispatch && dispatch({
    type: 'analytics/MERCHANDISE_PURCHASE_FAILED',
    payload: data
  });
};

export const trackStudioCartLearnMore = () => dispatch => {
  modsySegment.trackCartLearnMore();
  dispatch && dispatch({
    type: 'analytics/STUDIO_CART_LEARN_MORE',
    payload: {}
  });
};

/**
 * Tracking for auth success events
 */
export const authSuccess = ({
  userId,
  email,
  firstName,
  lastName,
  created,
  dateJoined,
  isAdmin
}) => dispatch => {
  modsyMixpanel.setLoggedin(
    userId,
    email,
    firstName,
    lastName,
    created,
    isAdmin || 0
  );
  if (userId) {
    modsySegment.trackAuth({
      userId,
      traits: {
        createdAt: dateJoined,
        email,
        firstName,
        isAdmin: isAdmin || false,
        landing: null,
        lastName
      }
    });
  }

  dispatch && dispatch({
    type: 'analytics/AUTH_SUCCESS',
    payload: { email, userId }
  });
};

/**
 * Track Website Header Click
 */
export const headerClick = ({ linkName }) => dispatch => {
  modsyMixpanel.headerClick(linkName);

  dispatch && dispatch({
    type: 'analytics/HEADER_CLICK',
    payload: { linkName }
  });
};

/**
 * Track Space Visit
 */
export const trackStudioHomepageVisit = ({
  admin = null,
  designId = null,
  designNumber = null,
  designStylist = '',
  packageType = '',
  spaceId = null,
  userId = null,
  analyticsPackageInfo = {}
} = {}) => (dispatch, getState) => {
  if (admin) {
    modsyMixpanel.trackSpaceVisitFromAdmin(spaceId);
  } else if (analyticsPackageInfo.shared) {
    modsyMixpanel.trackSpaceVisitOther(userId, spaceId);
    warehouse.spaceVisitShared(spaceId);
  } else {
    modsyMixpanel.trackSpaceVisit(spaceId);
    warehouse.spaceVisitOwner(spaceId);
  }

  const studioGenome = getUserStyleGenome(getState());

  const analyticsPackageData = {
    admin,
    designId,
    designNumber,
    designStylist,
    isSharedStudio: analyticsPackageInfo.shared,
    packagePrice: analyticsPackageInfo.packagePrice,
    packageType: analyticsPackageInfo.packageType,
    spaceId,
    studioGenome,
    userId
  };

  modsySegment.trackStudioVisit({ analyticsPackageData });

  dispatch && dispatch({
    type: 'analytics/STUDIO_HOMEPAGE_VISIT',
    payload: analyticsPackageData
  });
};

/**
 * Track Design Visit
 */
export const trackDesignVisit = ({ shared, designId }) => dispatch => {
  if (shared) {
    warehouse.designVisitShared(designId);
  } else {
    warehouse.designVisitOwner(designId);
  }

  dispatch && dispatch({
    type: 'analytics/DESIGN_VISIT',
    payload: { shared, designId }
  });
};

/**
 * Track Edit Click (navigate to Bessie)
 */
export const trackNavigateToBessie = ({
  design,
  activeSpace,
  packagePrice,
  spaceId
}) => dispatch => {
  const payload = {
    design,
    studio: activeSpace,
    packagePrice
  };
  modsySegment.trackBessieOpened(payload);

  modsyMixpanel.trackEditClick(spaceId);

  dispatch && dispatch({
    type: 'analytics/NAVIGATE_TO_BESSIE',
    payload: { spaceId }
  });
};

/**
 * Track Redesign Click
 */
export const trackRedesignRequest = ({
  space,
  design,
  packagePrice
}) => dispatch => {
  const data = {
    design_number: design && design.designIndex(),
    space_id: design && design.parentSpaceId()
  };

  const payload = {
    space,
    design,
    packagePrice
  };

  modsySegment.trackStudioRedesignRequestClicked(payload);
  modsyMixpanel.__mixpanelTrackEvent('Studio: Modsy-Edit Click', data);

  dispatch && dispatch({
    type: 'analytics/REDESIGN_REQUEST',
    payload: data
  });
};

/**
 * Track Add to Cart events.
 */
export const trackAddToCart = ({
  altProduct,
  design,
  enabledFilters,
  isProductAddOn = false,
  origin,
  packagePrice,
  product,
  promoCount,
  searchKey,
  searchProducts,
  selectedHotspotProduct,
  studio,
  userId
}) => dispatch => {
  const searchOrigin = ProductSearch.isFromPACSearch({ searchKey })
    ? 'general search'
    : (studio ? 'alternatives' : 'shop');

  const productAddOn = product;
  const lastSelectedProduct = isProductAddOn ? productAddOn : (altProduct || product);

  const data = {
    space_id: studio && studio.id(),
    design_number: design && design.designIndex(),
    origin,
    product_id: lastSelectedProduct && lastSelectedProduct.id(),
    promoCount,
    searchOrigin,
    source: lastSelectedProduct.retailer(),
    url: lastSelectedProduct.url(),
    price: lastSelectedProduct.currentPrice()
  };

  const event = 'Add to Cart';
  modsyMixpanel.__mixpanelTrackEvent(event, data);

  const referralProduct = altProduct || selectedHotspotProduct;

  const productIdOfPDPAlternativeReferral = getAlternativeID({ referralProduct, productInitiallySelected: product });

  const tileCardPlaceInProductGrid = fetchProductBrowserSearchIndex({
    product,
    selectedHotspotProduct,
    searchProducts
  });

  const payload = {
    design,
    origin,
    packagePrice,
    primaryReferralSource: isProductAddOn ? 'PDP_addon_product' : origin,
    product: lastSelectedProduct,
    productGridFilterNames: enabledFilters,
    productIdOfPDPAlternativeReferral,
    searchOrigin,
    studio,
    tileCardPlaceInProductGrid,
    userId
  };

  modsySegment.trackStudioAddToCart(payload);

  dispatch && dispatch({
    type: 'analytics/ADD_TO_CART',
    payload: { event, data }
  });
};

/**
 * Track Events
 */
export const trackEvent = ({ event, data }) => dispatch => {
  modsyMixpanel.__mixpanelTrackEvent(event, data);

  modsySegment.trackEvent({
    event,
    properties: {
      ...data
    }
  });

  dispatch && dispatch({
    type: 'analytics/EVENT',
    payload: { event, data }
  });
};

/**
 * Track Affirm Learn More Click
 */
export const trackAffirmLearnMore = ({ origin, productId, spaceId }) => dispatch => {
  modsyMixpanel.trackAffirmLearnMoreClick({ origin, productId, spaceId });

  dispatch && dispatch({
    type: 'analytics/AFFIRM_LEARN_MORE_CLICK',
    payload: { origin, spaceId, productId }
  });
};

/**
 * Track Studio Events
 */
export const trackMobileHamburgerMenuOptionSelect = ({
  linkName = ''
} = {}) => dispatch => {
  modsySegment.trackMobileHamburgerMenuOptionSelect({ linkName });
  dispatch(headerClick({ linkName }));
};

export const trackStudioHotSpotClicked = ({
  design,
  product,
  packagePrice,
  studio,
  userId
}) => dispatch => {
  const payload = {
    design,
    packagePrice,
    product,
    studio,
    userId
  };
  modsySegment.trackStudioHotSpotClicked(payload);
  dispatch(trackEvent({
    event: 'Studio: Hotspot Selection',
    data: {
      space_id: studio && studio.id(),
      design_id: design && design.id(),
      design_number: design && design.designIndex(),
      packagePrice,
      product_id: product && product.id()
    }
  }));
  dispatch && dispatch({
    type: 'analytics/STUDIO_HOTSPOT_CLICKED',
    payload: {
      design,
      packagePrice,
      product,
      studio,
      userId
    }
  });
};

export const trackTapOnMobileRender = ({
  hotspotStatus,
  spaceId,
  userId
}) => dispatch => {
  const payload = {
    hotspotStatus,
    spaceId,
    userId
  };
  modsySegment.trackStudioTapOnMobileRender(payload);
  dispatch && dispatch(trackEvent({
    event: `Studio: tap hotspots ${hotspotStatus}`,
    data: {
      space_id: spaceId,
      user_id: userId
    }
  }));
  dispatch && dispatch({
    type: `analytics/STUDIO_TAP_HOTSPOTS_${hotspotStatus.toUpperCase()}`,
    payload: {
      hotspotStatus,
      spaceId,
      userId
    }
  });
};

export const trackStudioPDPView = ({
  altProduct,
  product,
  referralSource
}) => (dispatch, getState) => {
  const state = getState();
  const design = getActiveDesign({ state });
  const studio = getActiveSpace({ state });
  const searchKey = getSearchKey(state);
  const enabledFilters = ProductSearch.getEnabledFiltersFor({ name: searchKey, state });
  const searchProducts = ProductSearch.getSearchResultFor({ name: searchKey, state });
  const selectedHotspotProduct = getSelectedHotspotProduct(state);
  const userId = getUserIsLoggedIn(state) && getUserId(state);

  const packageData = getPackagesData(state);

  const studioPackage = packageData.find(studioPackage => studioPackage.sku === studio.getPackageTypes()[0]);
  const packagePrice = studioPackage && studioPackage.price;

  const referralProduct = altProduct || selectedHotspotProduct;
  const productIdOfPDPAlternativeReferral = referralProduct && referralProduct.id() !== product.id()
    ? referralProduct.id()
    : null;

  const tileCardPlaceInProductGrid = fetchProductBrowserSearchIndex({
    product,
    selectedHotspotProduct,
    searchProducts
  });

  const searchOrigin = ProductSearch.isFromPACSearch({ searchKey })
    ? 'general search'
    : (studio
      ? (referralSource || 'alternatives')
      : 'shop');

  const payload = {
    design,
    product,
    packagePrice,
    productGridFilterNames: enabledFilters,
    productIdOfPDPAlternativeReferral,
    referralSource: searchOrigin,
    studio,
    tileCardPlaceInProductGrid,
    userId
  };

  modsySegment.trackStudioPDPView(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_PDP_VIEW',
    payload: {
      design,
      productGridFilterNames: enabledFilters,
      productIdOfPDPAlternativeReferral,
      product,
      searchOrigin,
      studio,
      tileCardPlaceInProductGrid,
      userId,
      packagePrice
    }
  });
};

export const trackStudioSwap = ({
  altProduct,
  design,
  enabledFilters,
  origin,
  product,
  searchKey,
  searchProducts,
  selectedHotspotProduct,
  studio,
  userId
}) => dispatch => {
  const referralProduct = altProduct || selectedHotspotProduct;
  const productIdOfPDPAlternativeReferral = referralProduct && referralProduct.id() !== product.id()
    ? referralProduct.id()
    : null;

  const productIdOfSwappedProduct = selectedHotspotProduct && product.id() !== selectedHotspotProduct.id()
    ? selectedHotspotProduct.id()
    : null;

  const tileCardPlaceInProductGrid = fetchProductBrowserSearchIndex({
    product,
    selectedHotspotProduct,
    searchProducts
  });

  const searchOrigin = ProductSearch.isFromPACSearch({ searchKey })
    ? 'general search' : 'alternatives';

  const payload = {
    design,
    origin,
    productGridFilterNames: enabledFilters,
    productIdOfPDPAlternativeReferral,
    productIdOfSwappedProduct,
    product,
    searchOrigin,
    studio,
    tileCardPlaceInProductGrid,
    userId
  };

  modsySegment.trackStudioSwap(payload);

  dispatch(trackEvent({
    event: `${origin} Swap Click`,
    data: {
      spaceId: studio.id(),
      replacedProductId: productIdOfSwappedProduct,
      swapInProductId: product.id()
    }
  }));
  dispatch && dispatch({
    type: 'analytics/STUDIO_SWAP',
    payload: {
      design,
      productGridFilterNames: enabledFilters,
      productIdOfPDPAlternativeReferral,
      productIdOfSwappedProduct,
      product,
      searchOrigin,
      studio,
      tileCardPlaceInProductGrid,
      userId
    }
  });
};

export const trackStudioPDPAlternativeFiltersUsed = (payload) => dispatch => {
  modsySegment.trackStudioPDPAlternativeFiltersUsed(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_PDP_ALTERNATIVE_FILTERS_USED',
    payload
  });
};

export const trackStudioPDPAlternativeProductsClicked = ({
  altProduct,
  design,
  pdpAltFilterNames,
  product,
  studio
}) => dispatch => {
  const payload = {
    altProduct,
    design,
    pdpAltFilterNames,
    product,
    studio
  };

  modsySegment.trackStudioPDPAlternativeProductsClicked(payload);
  dispatch(trackEvent({
    event: 'PDP - Alternate Product Click',
    data: {
      space_id: design && design.parentSpaceId(),
      design_number: design && design.designIndex(),
      product_id: product.id()
    }
  }));
  dispatch && dispatch({
    type: 'analytics/STUDIO_PDP_ALTERNATIVE_PRODUCTS_CLICKED',
    payload: {
      altProduct,
      design,
      pdpAltFilterNames,
      product,
      studio
    }
  });
};

export const trackStudioVariantClick = ({
  attributeType,
  attributeValue,
  index,
  product,
  studio,
  parentProduct
}) => (dispatch) => {
  // If available add the attribute type (color, dimensions) to the data
  const attributeTypeInfo = attributeType
    ? {
      attribute_type: attributeType,
      attribute_value: attributeValue
    }
    : {};

  const payload = {
    attributeTypeInfo,
    index,
    product,
    studio,
    parentProduct
  };

  modsySegment.trackStudioVariantClick(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_VARIANT_CLICK',
    payload
  });
};

export const trackStudioDesignFeedbackFreeForm = (payload) => dispatch => {
  modsySegment.trackStudioDesignFeedbackFreeForm(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_DESIGN_FEEDBACK_FREE_FORM',
    payload
  });
};

export const trackStudioDesignFeedbackChangesRequested = ({
  design,
  designFeedbackChangeSelection,
  designFeedbackScore,
  studio
}) => dispatch => {
  const payload = {
    design,
    designFeedbackChangeSelection,
    designFeedbackScore,
    studio
  };

  modsySegment.trackStudioDesignFeedbackChangesRequested(payload);

  trackEvent({
    event: 'Studio: Redesign Request Submitted',
    data: {
      design_number: design && design.designIndex(),
      selected_options: designFeedbackChangeSelection,
      space_id: design && design.parentSpaceId()
    }
  });

  dispatch && dispatch({
    type: 'analytics/STUDIO_DESIGN_FEEDBACK_CHANGES_REQUESTED',
    payload: {
      design,
      designFeedbackChangeSelection,
      designFeedbackScore,
      studio
    }
  });
};

export const trackStudioDisplayShowroom = (payload) => dispatch => {
  modsySegment.trackStudioDisplayShowroom(payload);
  dispatch && dispatch({
    type: 'analytics/STUDIO_DISPLAY_SHOWROOM',
    payload
  });
};

/**
 * Track Design Switching
 */
export const trackDesignSwitch = (incomingData) =>
  (dispatch, getState) => {
    const state = getState();
    const isMobile = state.device.isMobile;
    const data = {
      ...incomingData,
      isMobile
    };
    const event = 'Studio: Switch Designs';
    modsyMixpanel.__mixpanelTrackEvent(event, data);
    modsySegment.trackDesignSwitch(data);

    dispatch && dispatch({
      type: 'analytics/STUDIO_SWITCH_DESIGNS',
      payload: { event, data }
    });
  };

export const trackPPOVisionMaxCharacters = (data) => dispatch => {
  const event = 'PPO VISION: Max Characters';
  modsyMixpanel.__mixpanelTrackEvent(event, data);

  dispatch && dispatch({
    type: 'analytics/PPO_FORM_MAX_CHARACTERS',
    payload: { event, data }
  });
};

/**
 * Showroom event tracking.
 */
export const trackShowroomEvent = ({
  page, // [required] The page name where event occurred
  click, // [optional] The CTA that was clicked if applicable
  error, // [optional] An error description if applicable,
  meta // [optional] Page specific props that are passed through to Mixpanel
}) => dispatch => {
  const event = 'Showroom';
  let data = { page };
  if (click) { data.click = click; }
  if (error) { data.error = error; }
  if (meta) {
    data = { ...data, ...meta };
  }

  modsyMixpanel.__mixpanelTrackEvent(event, data);

  dispatch && dispatch({
    type: 'analytics/SHOWROOM_EVENT',
    payload: { event, data }
  });
};

/**
 * Initialize Facebook Pixel
 */
export const initializeModsyFBPixel = (userInfo = {}) => dispatch => {
  modsyFacebook.initializeModsyPixel(userInfo);
  modsyFacebook.trackPageView();

  dispatch && dispatch({
    type: 'analytics/INITIALIZE_FACEBOOK_PIXEL'
  });
};

/**
 * Initialize Facebook Pixel
 */
export const initializeModsyTTQPixel = (userInfo = {}) => dispatch => {
  modsyTikTok.initializeTikTokPixel();

  dispatch && dispatch({
    type: 'analytics/INITIALIZE_TIKTOK_PIXEL'
  });
};

/**
* Track Like/dislike of product.
*/
export const trackProductLikeDislikeClick = ({
  addlTrackingProps = {},
  dislikeReason = '',
  isFromShop = false,
  likeStatus = 0, // like or dislike
  origin = '' // studio_hotspot, studio_pdp, shop_pdp, studio_tile_card, shop_tile_card
} = {}) => (dispatch, getState) => {
  const {
    altProduct,
    enabledFiltersFromShop,
    product,
    productInitiallySelected,
    tileCardPlaceInShopProductGrid
  } = addlTrackingProps;

  const state = getState();
  const design = getActiveDesign({ state });
  const email = getUserEmail(state);
  const userId = getUserId(state);
  const userStyleGenome = getUserStyleGenome(state);

  const searchKey = getSearchKey(state);
  const enabledFilters = enabledFiltersFromShop || ProductSearch.getEnabledFiltersFor({ name: searchKey, state });
  const searchProducts = ProductSearch.getSearchResultFor({ name: searchKey, state });
  const selectedHotspotProduct = getSelectedHotspotProduct(state);
  const isMobile = state.device.isMobile;

  const tileCardPlaceInProductGrid = getCardPlaceInProductGrid({
    isFromShop,
    origin,
    product,
    searchProducts,
    selectedHotspotProduct,
    tileCardPlaceInShopProductGrid
  });

  const isFromPACSearch = ProductSearch.isFromPACSearch({ searchKey });
  const searchOrigin = getSearchOrigin({ isFromPACSearch, isFromShop });

  const referralProduct = altProduct || selectedHotspotProduct;
  const productIdOfPDPAlternativeReferral = getAlternativeID({ referralProduct, productInitiallySelected });

  const data = {
    designId: design && design.id(),
    dislikeReason: dislikeReason || '',
    email,
    enabledFilters,
    isShowroom: design && design.isShowroomDesign(),
    likeStatus,
    origin,
    productIdOfPDPAlternativeReferral,
    product,
    searchOrigin,
    spaceId: design && design.parentSpaceId(),
    timestamp: Date.now(),
    tileCardPlaceInProductGrid,
    userId,
    userStyleGenome,
    isMobile
  };

  const event = 'Like/Dislike product';
  modsyMixpanel.__mixpanelTrackEvent(event, data);
  modsySegment.trackProductLikeDislikeClick(data);

  dispatch && dispatch({
    type: 'analytics/USER_FAVORITE_LIKE',
    payload: { event, data }
  });
};

/**
 * Helper method to get tile card position in grid.
 */
const getCardPlaceInProductGrid = ({
  isFromShop,
  origin,
  product,
  searchProducts,
  selectedHotspotProduct,
  tileCardPlaceInShopProductGrid
} = {}) => {
  switch (true) {
    case origin === 'shop_pdp':
      return -1;
    case origin === 'shop_tile_card':
      return tileCardPlaceInShopProductGrid;
    case !isFromShop:
      return fetchProductBrowserSearchIndex({
        product,
        selectedHotspotProduct,
        searchProducts
      });
    default:
      return -1;
  }
};

/**
 * Helper method to get search origin of an event.
 */
const getSearchOrigin = ({
  isFromPACSearch,
  isFromShop
} = {}) => {
  switch (true) {
    case isFromPACSearch && !isFromShop:
      return 'general search';
    case !isFromPACSearch && !isFromShop:
      return 'alternatives';
    case isFromShop:
      return 'shop';
    default:
      return '';
  }
};

/**
 * Track tab selection
 */
export const trackTabSelection = ({
  design,
  email,
  studio,
  tabType,
  userId
}) => (dispatch, getState) => {
  const state = getState();
  const isMobile = state.device.isMobile;

  const data = {
    design,
    email,
    studio,
    tabType,
    userId,
    isMobile
  };

  const mixPanelData = {
    designId: design && design.id(),
    email,
    spaceId: studio && studio.id(),
    tabType,
    userId,
    isMobile
  };

  const event = `Studio: ${tabType} Tab Selected`;
  modsyMixpanel.__mixpanelTrackEvent(event, mixPanelData);
  modsySegment.trackTabSelection({
    design,
    email,
    studio,
    tabType,
    userId,
    isMobile
  });

  dispatch && dispatch({
    type: 'analytics/STUDIO_TAB_SELECTION',
    payload: { event, data }
  });
};

/**
 * Track Show View All Designs Pullover Tab
 */
export const trackShowViewAllDesigns = ({
  design,
  email,
  studio,
  userId
}) => (dispatch, getState) => {
  const state = getState();
  const isMobile = state.device.isMobile;

  const data = {
    design,
    email,
    isMobile,
    studio,
    userId
  };

  const mixPanelData = {
    designId: design && design.id(),
    email,
    isMobile,
    spaceId: studio && studio.id(),
    userId
  };

  const event = 'Studio: View All Designs';
  modsyMixpanel.__mixpanelTrackEvent(event, mixPanelData);
  modsySegment.trackShowViewAllDesigns({ ...data });

  dispatch && dispatch({
    type: 'analytics/STUDIO_VIEW_ALL_DESIGNS',
    payload: { event, data }
  });
};

/**
 * Track show all liked products.
 */
export const trackShowFavorites = ({
  design,
  email,
  studio,
  userId
}) => (dispatch, getState) => {
  const state = getState();
  const isMobile = state.device.isMobile;

  const data = {
    design,
    email,
    isMobile,
    studio,
    userId
  };

  const mixPanelData = {
    designId: design && design.id(),
    spaceId: studio && studio.id(),
    email,
    isMobile,
    userId
  };

  const event = 'Studio: Show list of favorites';
  modsyMixpanel.__mixpanelTrackEvent(event, mixPanelData);
  modsySegment.trackShowFavorites({ ...data });

  dispatch && dispatch({
    type: 'analytics/FAVORITES_LIST',
    payload: { event, data }
  });
};

/**
 * Track text search
 */
export const trackTextSearchEvent = ({
  addlTrackingProps,
  productId,
  searchValue
}) => dispatch => {
  const { designId, numberOfResults, spaceId, userId } = addlTrackingProps;
  const data = {
    designId,
    numberOfResults,
    productId,
    searchValue,
    spaceId,
    userId
  };

  const event = 'Studio: Text searched';
  modsyMixpanel.__mixpanelTrackEvent(event, data);
  modsySegment.trackTextSearchSubmitted(data);

  dispatch && dispatch({
    type: 'analytics/STUDIO_TEXT_SEARCH',
    payload: { event, data }
  });
};

export const trackStudioPDPDisplayTooltip = () => dispatch => {
  // user hovers/clicks ⓘ  (info) in PDP card
  modsySegment.trackStudioPDPDisplayTooltip();
};

export const trackStudioPDPDisplayTooltipLearnMore = () => dispatch => {
  // user clicks on Learn More link in PDP tooltip
  modsySegment.trackStudioPDPDisplayTooltipLearnMore();
};

export const trackStudioBannerClicked = () => dispatch => {
  // user clicks on Banner in Studio Mobile View
  modsySegment.trackStudioBannerClicked();
};

export const track2dToggleClicked = ({ enable2d }) => dispatch => {
  // user clicks on 2d Catalog toggle
  modsySegment.track2dToggleClicked({ enable2d });
};

export const trackStudioBannerCTAClicked = (CTAIndex) => dispatch => {
  // user clicks on Banner Links in Studio Desktop View
  modsySegment.trackStudioBannerCTAClicked(CTAIndex);
};

export const trackHeroCTAClick = () => dispatch => {
  modsySegment.trackHeroCTAClick();

  dispatch(trackEvent({
    event: 'Homepage: Hero Get Started Click'
  }));
};

export const trackDesignPackagesCTAClick = () => dispatch => {
  modsySegment.trackDesignPackagesCTAClick();
  dispatch(trackEvent({
    event: 'Homepage: Design Packages Start Your Project Click'
  }));
};

export const trackUnlimitedRevCTAClick = () => dispatch => {
  modsySegment.trackUnlimitedRevCTAClick();
  dispatch(trackEvent({
    event: 'Homepage: Unlimited Rev Start Your Project Click'
  }));
};

export const trackBottomCTAClick = () => dispatch => {
  modsySegment.trackBottomCTAClick();
  dispatch(trackEvent({
    event: 'Homepage: Bottom CTA Get Started Click'
  }));
};

export const trackPackageSelectCTAClick = ({packageSku, packagePrice}) => dispatch => {
  modsySegment.trackPackageSelectCTAClick({packageSku, packagePrice});
  modsyTatari.trackPackageSelectCTAClick({packageSku, packagePrice});
  dispatch(trackEvent({
    event: 'Homepage: Package Select Click',
    data: {
      package_type: packageSku,
      package_price: packagePrice
    }
  }));
};

export const trackStartShowroomClick = () => dispatch => {
  modsySegment.trackStartShowroomClick();
  dispatch(trackEvent({
    event: 'Homepage: Get Your Free Showroom Click'
  }));
};

export const trackAllPackageSelect = (payload) => {
  modsyFacebook.trackDesignPackageSelect(payload);
  modsyFacebook.trackAddPackageToCart(payload);
  modsyTikTok.trackAddPackageToCart(payload);
};

export const trackComparePackagesClick = () => dispatch => {
  modsySegment.trackComparePackagesClick();
  dispatch(trackEvent({
    event: 'Design Packages: Compare Packages Click'
  }));
};

export const trackGetStartedQuiz = ({stepNum}) => dispatch => {
  modsyMixpanel.trackGetStartedQuiz({stepNum});

  let action = '';
  switch (stepNum) {
    case 0:
      action = 'Funnel Entry';
      break;

    case 1:
      action = 'Room Select';
      break;

    case 2:
      action = 'Goal Select';
      break;

    case 3:
      break; // Budget step no longer in quiz

    case 4:
      action = 'Furniture Select';
      break;

    case 5:
      action = 'Style Select'; // This is the Recs page
      break;

    case 6:
      // This is the Account page and it only gets hit if the user is not
      // already logged in. Thus we need to send a Style Select event for
      // both the Recs page and the Account page.
      action = 'Style Select';
      break;

    case 7:
      action = 'Package select'; // This is the Checkout page
      break;

    default:
      // Note: the Package Purchase and User Signup events are sent else where with addl metadata
      break;
  }

  if (action) {
    modsyGoogle.trackEvent('Customer Conversion', action);
  }

  dispatch(trackEvent({
    event: 'Get Started Quiz',
    data: {stepNum}
  }));
};

export const trackBrowseCatalogClick = ({ source }) => dispatch => {
  modsySegment.trackShopBrowseCatalogClick({ source });
};

export const trackShopLandingView = () => dispatch => {
  modsySegment.trackShopLandingView();
};

export const trackShopSearch = (data) => dispatch => {
  modsySegment.trackShopSearch(data);
};

export const trackShopPDPView = (data) => dispatch => {
  modsySegment.trackShopPDPView(data);
};

export const trackShopAddToCart = (data) => dispatch => {
  modsySegment.trackShopAddToCart(data);
};

export const trackShopRemoveFromCart = (data) => dispatch => {
  modsySegment.trackShopRemoveFromCart(data);
};

export const trackStudioCartPageVisited = (data) => dispatch => {
  modsySegment.trackStudioCartPageVisited(data);
};

export const trackStudioReviewOrderCtaClicked = (data) => dispatch => {
  modsySegment.trackStudioReviewOrderCtaClicked(data);
};

export const trackCheckoutConfirmationCtaClicked = (data) => dispatch => {
  modsySegment.trackCheckoutConfirmationCtaClicked(data);
};

export const trackCheckoutAddressError = (data) => dispatch => {
  modsySegment.trackCheckoutAddressError(data);
};

export const trackCheckoutPaymentFormOpened = (data) => dispatch => {
  modsySegment.trackCheckoutPaymentFormOpened(data);
};

export const trackCheckoutShippingAddressInput = (data) => dispatch => {
  modsySegment.trackCheckoutShippingAddressInput(data);
};

export const trackMicroShopBannerClick = ({ microshopName }) => dispatch => {
  modsySegment.trackMicroShopBannerClick({ microshopName });
};

export const trackShopCategoryClick = (data) => dispatch => {
  modsySegment.trackShopCategoryClick(data);
};

/**
 * Track Full screen button click
 */
export const trackFullscreenClick = ({
  spaceId,
  designId,
  designNumber,
  renderNumber
}) => dispatch => {
  const payload = {
    spaceId,
    designId,
    designNumber,
    renderNumber
  };
  modsySegment.trackStudioRenderFullscreenClick(payload);

  dispatch && dispatch({
    type: 'analytics/FULLSCREEN_CLICK',
    payload
  });
};

/**
 * Track 360 render open
 */
export const track360Click = ({
  spaceId,
  designId,
  designNumber,
  renderNumber
}) => dispatch => {
  const payload = {
    spaceId,
    designId,
    designNumber,
    renderNumber
  };
  modsySegment.trackStudio360RenderClick(payload);

  dispatch && dispatch({
    type: 'analytics/360_CLICK',
    payload
  });
};

// View Products
export const trackViewProductsClick = ({
  studio,
  design,
  origin,
  userId
}) => dispatch => {
  const payload = {
    studio,
    design,
    origin,
    userId
  };
  modsySegment.trackViewProductsClick(payload);

  dispatch && dispatch({
    type: 'analytics/VIEW_PRODUCTS_CLICK',
    payload
  });
};

// More Menu
export const trackMoreMenuOpen = ({
  studio,
  design,
  userId
}) => dispatch => {
  const payload = {
    studio,
    design,
    userId
  };
  modsySegment.trackMoreMenuOpen(payload);

  dispatch && dispatch({
    type: 'analytics/MORE_MENU_CLICK',
    payload
  });
};

// Share (from More Menu)
export const trackShareFromMoreMenu = ({
  studio,
  design,
  userId
}) => dispatch => {
  const payload = {
    studio,
    design,
    userId
  };
  modsySegment.trackShareFromMoreMenu(payload);

  dispatch && dispatch({
    type: 'analytics/MORE_MENU_SHARE_CLICK',
    payload
  });
};

// View Designer Note (from More Menu)
export const trackViewDesignerNoteFromMoreMenu = ({
  studio,
  design,
  userId
}) => dispatch => {
  const payload = {
    studio,
    design,
    userId
  };
  modsySegment.trackViewDesignerNoteFromMoreMenu(payload);

  dispatch && dispatch({
    type: 'analytics/MORE_MENU_NOTE_CLICK',
    payload
  });
};

// Delete (from More Menu)
export const trackDeleteDesignFromMoreMenu = ({
  studio,
  design,
  userId
}) => dispatch => {
  const payload = {
    studio,
    design,
    userId
  };
  modsySegment.trackDeleteDesignFromMoreMenu(payload);

  dispatch && dispatch({
    type: 'analytics/MORE_MENU_DELETE_CLICK',
    payload
  });
};

// Click Shop from studio
export const trackShopWidgetClick = ({
  studio,
  userId
}) => dispatch => {
  const payload = {
    studio,
    userId
  };
  modsySegment.trackShopWidgetClick(payload);

  dispatch && dispatch({
    type: 'analytics/SHOP_WIDGET_CLICK',
    payload
  });
};

// Space selector switch to different space
export const trackSpaceSelectorNavigate = ({
  studio,
  design,
  userId,
  spaceIdSelected
}) => dispatch => {
  const payload = {
    studio,
    design,
    userId,
    spaceIdSelected
  };
  modsySegment.trackSpaceSelectorNavigate(payload);

  dispatch && dispatch({
    type: 'analytics/SPACE_SELECTOR_NAVIGATE',
    payload
  });
};

/**
 * pageviews are automatically tracked by Google Analytics but they are all treated as unique
 * because of the spaceId. So we're tracking them with this event ignoring the spaceId.
 * memoization means we only call this if the url has changed
 */
export const trackPPOPageview = ({
  url = '',
  action = ''
}) => dispatch => {
  memoize((label) => {
    modsyGoogle.trackEvent('PPO', action, label);
  })(url);

  dispatch({
    type: 'analytics/TRACK_PPO_SPACE_VIEW',
    payload: {category: 'PPO', action, label: url}
  });
};

export const trackStyleQuizPasswordlessAB = skipPassword => {
  modsySegment.trackEvent({
    event: 'WEB_EXPERIMENT_AB',
    properties: {
      marketing_variant_ab: skipPassword
        ? 'stylequiz_passwordless_variant'
        : 'stylequiz_passwordless_control'
    }
  });
};

export const trackStyleQuizVariantAB = isShortStyleQuiz => {
  modsySegment.trackEvent({
    event: 'WEB_EXPERIMENT_AB',
    properties: {
      marketing_variant_ab: isShortStyleQuiz
        ? 'stylequiz_short_variant'
        : 'stylequiz_short_control'
    }
  });
};

export const trackCheckoutVersion = version => {
  modsySegment.trackEvent({
    event: 'WEB_EXPERIMENT_AB',
    properties: {
      marketing_variant_ab: version === 'new'
        ? 'package_checkout_variant'
        : 'package_checkout_control'
    }
  });
};

export const trackValidationError = data => {
  modsySegment.trackEvent({
    event: 'FE_CHECKOUT_VALIDATION_ERROR',
    properties: data
  });
};
